/**
 * Order Form Schemas
 * Zod validation schemas for order flow using discriminated unions
 *
 * Architecture:
 * - Phase 1 (new_delivery): Full flow with all fields required
 * - Phase 2 (existing_container): Quick flow with pre-filled data from lookup
 * - Intent-only: Partial validation for step 1 before intent is selected
 */

import { z } from 'zod';

// ============================================================================
// Step Schemas (reusable validation units)
// ============================================================================

/**
 * Service selection step schema (Phase 1)
 */
export const serviceSelectionSchema = z.object({
  orderType: z.enum(['delivery', 'swap', 'pickup'], {
    errorMap: () => ({ message: 'Please select an order type' }),
  }),
  containerSize: z.string().min(1, 'Please select a container size'),
  materialType: z.string().min(1, 'Please select a material type'),
});

/**
 * Schedule step schema (Phase 1)
 */
export const scheduleSchema = z.object({
  deliveryDate: z.string().min(1, 'Please select a delivery date'),
});

/**
 * Location step schema (Phase 1)
 */
export const locationSchema = z.object({
  street: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(2, 'State is required').max(2, 'Use 2-letter state code'),
  zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Valid ZIP code required (e.g., 93446 or 93446-1234)'),
  country: z.string().optional().default('US'),
});

/**
 * Details step schema (both phases)
 */
export const detailsSchema = z.object({
  placementInstructions: z.string().optional(),
});

/**
 * PRRO (Pay-As-You-Throw Regulatory & Operational) fields schema
 * Optional fields for providers with PRRO configuration enabled
 *
 * All fields are optional to maintain backward compatibility with
 * non-PRRO providers. Frontend components will conditionally render
 * based on provider configuration.
 */
export const prroFieldsSchema = z.object({
  // Pricing model selection (flat_rate, exact_tonnage, specialty)
  pricingModelId: z.string().optional(),

  // Material contamination tracking
  hasTreatedWood: z.boolean().optional(),
  contaminationAcknowledged: z.boolean().optional(),

  // Estimated tonnage for exact tonnage pricing
  estimatedTonnage: z.number().optional(),

  // Acknowledged terms (e.g., contamination warnings)
  acknowledgedTerms: z.array(z.string()).optional(),

  // Selected materials for specialty flows
  selectedMaterials: z.array(z.string()).optional(),

  // Estimated weight in tons
  estimatedWeightTons: z.number().optional(),
}).optional();

/**
 * Customer intent schema
 */
export const customerIntentSchema = z.object({
  customerIntent: z.enum(['new_delivery', 'existing_container']).optional(),
});

/**
 * Quick schedule schema (Phase 2 - for swap/pickup with existing address)
 * Must have existing order context and selected action
 */
export const quickScheduleSchema = z.object({
  deliveryDate: z.string().min(1, 'Please select a date'),
  additionalNotes: z.string().optional(),
  // Context requirements for Phase 2
  existingOrderId: z.string().min(1, 'Container context required'),
  orderType: z.enum(['swap', 'pickup'], {
    errorMap: () => ({ message: 'Please select swap or pickup' }),
  }),
});

// ============================================================================
// Phase-Specific Schemas (discriminated union members)
// ============================================================================

/**
 * Base fields common to all flows
 */
const baseSchema = z.object({
  deliveryNotes: z.string().optional(),
});

/**
 * Phase 1: New delivery flow
 * Full multi-step flow for customers ordering a new container
 *
 * Required fields:
 * - Container selection (containerSize, materialType)
 * - Schedule (deliveryDate)
 * - Location (street, city, state, zipCode)
 * - Order type is always 'delivery'
 *
 * Optional fields:
 * - placementInstructions
 * - deliveryNotes
 * - PRRO fields (when provider has PRRO enabled)
 *
 * Explicitly excludes:
 * - existingOrderId (not applicable for new deliveries)
 */
const newDeliverySchema = baseSchema.extend({
  customerIntent: z.literal('new_delivery'),
  orderType: z.literal('delivery'),

  // Container selection - REQUIRED
  containerSize: z.string().min(1, 'Please select a container size'),
  materialType: z.string().min(1, 'Please select a material type'),

  // Schedule - REQUIRED
  deliveryDate: z.string().min(1, 'Please select a delivery date'),

  // Location - REQUIRED
  street: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(2, 'State is required').max(2, 'Use 2-letter state code'),
  zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Valid ZIP code required'),
  country: z.string().default('US'),

  // Details - OPTIONAL
  placementInstructions: z.string().optional(),

  // PRRO fields - OPTIONAL (backward compatible)
  pricingModelId: z.string().optional(),
  hasTreatedWood: z.boolean().optional(),
  contaminationAcknowledged: z.boolean().optional(),
  estimatedTonnage: z.number().optional(),
  acknowledgedTerms: z.array(z.string()).optional(),
  selectedMaterials: z.array(z.string()).optional(),
  estimatedWeightTons: z.number().optional(),

  // Additional services - OPTIONAL
  selectedServices: z.array(z.object({
    serviceId: z.string(),
    quantity: z.number().min(1).default(1),
  })).optional(),

  // Phase 1 doesn't have existingOrderId - explicitly undefined
  existingOrderId: z.undefined().optional(),
});

/**
 * Phase 2: Existing container flow
 * Quick flow for customers with existing containers doing swap/pickup
 *
 * Required fields:
 * - Lookup (existingOrderId - must be set from lookup step)
 * - Action (orderType - either 'swap' or 'pickup')
 * - Schedule (deliveryDate)
 *
 * Pre-filled from lookup (still required in schema):
 * - containerSize
 * - street, city, state, zipCode
 *
 * Optional fields:
 * - materialType (may change with swap)
 * - placementInstructions
 * - deliveryNotes
 */
const existingContainerSchema = baseSchema.extend({
  customerIntent: z.literal('existing_container'),

  // Lookup - REQUIRED (must be set before proceeding)
  existingOrderId: z.string().min(1, 'Container must be looked up first'),

  // Action selection - REQUIRED (swap or pickup)
  orderType: z.enum(['swap', 'pickup'], {
    required_error: 'Please select swap or pickup',
  }),

  // Quick schedule - REQUIRED
  deliveryDate: z.string().min(1, 'Please select a date'),

  // Pre-filled from lookup (still need them in form)
  containerSize: z.string().min(1, 'Container size required'),
  street: z.string().min(1, 'Street address required'),
  city: z.string().min(1, 'City required'),
  state: z.string().min(2, 'State required').max(2, 'Use 2-letter state code'),
  zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Valid ZIP code required'),
  country: z.string().default('US'),

  // Optional fields
  materialType: z.string().optional(), // Might change with swap
  placementInstructions: z.string().optional(),

  // Additional services - OPTIONAL
  selectedServices: z.array(z.object({
    serviceId: z.string(),
    quantity: z.number().min(1).default(1),
  })).optional(),
});

/**
 * Intent-only schema (for step 1 before intent is selected)
 * All fields are optional at this stage
 */
const intentOnlySchema = z.object({
  customerIntent: z.enum(['new_delivery', 'existing_container']).optional(),
  // All other fields optional until intent is selected
  orderType: z.string().optional(),
  containerSize: z.string().optional(),
  materialType: z.string().optional(),
  deliveryDate: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
  placementInstructions: z.string().optional(),
  deliveryNotes: z.string().optional(),
  existingOrderId: z.string().optional(),
  // PRRO fields - optional
  pricingModelId: z.string().optional(),
  hasTreatedWood: z.boolean().optional(),
  contaminationAcknowledged: z.boolean().optional(),
  estimatedTonnage: z.number().optional(),
  acknowledgedTerms: z.array(z.string()).optional(),
  selectedMaterials: z.array(z.string()).optional(),
  estimatedWeightTons: z.number().optional(),
  // Additional services - optional
  selectedServices: z.array(z.object({
    serviceId: z.string(),
    quantity: z.number().min(1).default(1),
  })).optional(),
});

// ============================================================================
// Main Order Schema (discriminated union)
// ============================================================================

/**
 * Main order schema using custom discriminated validation
 *
 * TypeScript will properly infer the type based on customerIntent:
 * - If customerIntent === 'new_delivery', validates with newDeliverySchema
 * - If customerIntent === 'existing_container', validates with existingContainerSchema
 * - If customerIntent is undefined, validates with intentOnlySchema (lenient)
 *
 * We use superRefine to properly handle the discrimination and provide
 * appropriate validation based on the customerIntent value
 */
export const orderSchema = z
  .object({
    customerIntent: z.enum(['new_delivery', 'existing_container']).optional(),
  })
  .passthrough() // Allow all fields to pass through
  .superRefine((data, ctx) => {
    // If no customer intent, allow any partial data (step 1)
    if (!data.customerIntent) {
      // Validate with intentOnlySchema
      const result = intentOnlySchema.safeParse(data);
      if (!result.success) {
        result.error.errors.forEach((err) => ctx.addIssue(err));
      }
      return;
    }

    // If customer intent is set, validate with appropriate schema
    if (data.customerIntent === 'new_delivery') {
      const result = newDeliverySchema.safeParse(data);
      if (!result.success) {
        result.error.errors.forEach((err) => ctx.addIssue(err));
      }
    } else if (data.customerIntent === 'existing_container') {
      const result = existingContainerSchema.safeParse(data);
      if (!result.success) {
        result.error.errors.forEach((err) => ctx.addIssue(err));
      }
    }
  });

// ============================================================================
// Step-Specific Validation Schemas
// ============================================================================

/**
 * Step-by-step validation schemas for each phase
 * Used by OrderFlow component to validate individual steps
 */
export const stepSchemas = {
  phase1: {
    intent: intentOnlySchema,
    schedule: scheduleSchema,
    container: serviceSelectionSchema.omit({ orderType: true }).extend({
      orderType: z.literal('delivery'), // Phase 1 is always delivery
    }),
    location: locationSchema,
    details: detailsSchema,
  },
  phase2: {
    intent: intentOnlySchema,
    lookup: z.object({
      existingOrderId: z.string().min(1, 'Please enter a container number'),
    }),
    action: z.object({
      orderType: z.enum(['swap', 'pickup'], {
        required_error: 'Please select swap or pickup',
      }),
    }),
    schedule: z.object({
      deliveryDate: z.string().min(1, 'Please select a date'),
    }),
    details: detailsSchema,
  },
} as const;

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard for Phase 1 (new delivery flow)
 */
export function isNewDeliveryFlow(data: Partial<OrderForm>): boolean {
  return data.customerIntent === 'new_delivery';
}

/**
 * Type guard for Phase 2 (existing container flow)
 */
export function isExistingContainerFlow(data: Partial<OrderForm>): boolean {
  return data.customerIntent === 'existing_container';
}

/**
 * Type guard to check if data has existing order ID
 * Narrows type to ensure existingOrderId is defined
 */
export function hasExistingOrder(
  data: Partial<OrderForm>
): data is { existingOrderId: string } {
  return !!data.existingOrderId && data.existingOrderId.length > 0;
}

/**
 * Type guard to check if customer intent is selected
 */
export function hasCustomerIntent(
  data: Partial<OrderForm>
): data is { customerIntent: 'new_delivery' | 'existing_container' } {
  return (
    data.customerIntent === 'new_delivery' ||
    data.customerIntent === 'existing_container'
  );
}

// ============================================================================
// Type Exports
// ============================================================================

export type ServiceSelectionForm = z.infer<typeof serviceSelectionSchema>;
export type ScheduleForm = z.infer<typeof scheduleSchema>;
export type QuickScheduleForm = z.infer<typeof quickScheduleSchema>;
export type LocationForm = z.infer<typeof locationSchema>;
export type DetailsForm = z.infer<typeof detailsSchema>;

// Phase-specific types for better type safety
export type NewDeliveryForm = z.infer<typeof newDeliverySchema>;
export type ExistingContainerForm = z.infer<typeof existingContainerSchema>;
export type IntentOnlyForm = z.infer<typeof intentOnlySchema>;

/**
 * OrderForm type
 * Union of all possible form states based on customer intent
 *
 * This properly types the form data based on the discriminator:
 * - When customerIntent is 'new_delivery', form matches NewDeliveryForm
 * - When customerIntent is 'existing_container', form matches ExistingContainerForm
 * - When customerIntent is undefined, form matches IntentOnlyForm
 */
export type OrderForm = NewDeliveryForm | ExistingContainerForm | IntentOnlyForm;
